import { useEffect, useState } from 'react';

/**
 * Debounces a string value, delaying updates until the user has stopped
 * changing it for the specified delay period.
 *
 * Useful for reducing the frequency of expensive operations (API calls,
 * filtering) that are triggered by rapid user input.
 *
 * @param value - The input value to debounce.
 * @param delay - Debounce delay in milliseconds.
 * @returns The debounced value, updated only after `delay` ms of inactivity.
 *
 * @example
 * const debouncedSearch = useDebounce(searchTerm, 300);
 * useEffect(() => { fetchResults(debouncedSearch); }, [debouncedSearch]);
 */
function useDebounce(value: string, delay: number): any {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
}

export default useDebounce;
